/**
 * MODULE: domain/sales-presentations/frontend/components/PresentationPostModal
 * PURPOSE: E4-4 — guided Post flow: pick channel (if many), preview + note, success or error with retry.
 * DEPENDENCIES: flowbite-react Modal, presentationsApi, SlideCanvasThumbnail, normalizeRenderedSlide
 * PUBLIC_API: PresentationPostModal
 * AGENT_SAFETY: SAFE
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Textarea } from 'flowbite-react';
import {
  ChatBubbleLeftRightIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  ArrowLeftIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import type { PublishChannel, PublishPresentationResponse } from '../api/client';
import { presentationsApi } from '../api/client';
import type { RenderedSlide } from '../api/types';
import { SlideCanvasThumbnail } from './SlideThumbnailStrip';
import { normalizeRenderedSlide } from '../utils/normalizeContentSchema';
import { formatPostedAgo } from '../utils/presentationPublishStorage';

const MAX_MESSAGE = 300;
const SETTINGS_INTEGRATIONS_HREF = '/settings?tab=integrations';

function ChannelGlyph({ iconKey }: { iconKey: string }) {
  const cls = 'h-8 w-8 shrink-0 text-gray-600 dark:text-gray-300';
  if (iconKey === 'slack') return <ChatBubbleLeftRightIcon className={cls} aria-hidden />;
  if (iconKey === 'hubspot') return <BuildingOffice2Icon className={cls} aria-hidden />;
  return <EnvelopeIcon className={cls} aria-hidden />;
}

function parsePostError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const st = err.response?.status;
    const data = err.response?.data as { detail?: unknown } | undefined;
    const d = data?.detail;
    if (st === 401 && d && typeof d === 'object' && d !== null && 'code' in d) {
      const code = (d as { code?: string }).code;
      if (code === 'CHANNEL_AUTH_EXPIRED') {
        return (d as { message?: string }).message ?? 'Connection expired — reconnect in Settings.';
      }
    }
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object' && 'message' in d) return String((d as { message?: unknown }).message);
  }
  return 'Something went wrong — try again.';
}

export interface PresentationPostModalProps {
  show: boolean;
  onClose: () => void;
  presentationId: string;
  deckTitle: string;
  firstSlide: RenderedSlide | null;
  channels: PublishChannel[];
  channelsLoading: boolean;
  onPublished: (meta: PublishPresentationResponse) => void;
}

export function PresentationPostModal({
  show,
  onClose,
  presentationId,
  deckTitle,
  firstSlide,
  channels,
  channelsLoading,
  onPublished,
}: PresentationPostModalProps) {
  const titleId = useId();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState<{ ok: true; data: PublishPresentationResponse } | { ok: false; msg: string } | null>(
    null
  );

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(() => {
    if (!show) return;
    clearCloseTimer();
    setStep(1);
    setSelectedId(null);
    setMessage('');
    setPosting(false);
    setResult(null);
  }, [show]);

  useEffect(() => {
    if (!show || channelsLoading) return;
    if (channels.length === 1) {
      setSelectedId(channels[0].channelId);
      setStep(2);
    }
  }, [show, channelsLoading, channels]);

  const selected = channels.find((c) => c.channelId === selectedId) ?? null;

  const runPost = useCallback(async () => {
    if (!selectedId) return;
    setPosting(true);
    setResult(null);
    try {
      const data = await presentationsApi.publishPresentation(presentationId, {
        channelId: selectedId,
        message: message.trim() || undefined,
      });
      onPublished(data);
      setResult({ ok: true, data });
      setStep(3);
    } catch (err) {
      setResult({ ok: false, msg: parsePostError(err) });
      setStep(3);
    } finally {
      setPosting(false);
    }
  }, [message, onPublished, presentationId, selectedId]);

  const goPreview = () => {
    if (!selectedId) return;
    setStep(2);
  };

  const reconnectBlock = (
    <p className="text-sm text-amber-800 dark:text-amber-200">
      Your connection may have expired.{' '}
      <a href={SETTINGS_INTEGRATIONS_HREF} className="font-semibold underline">
        Reconnect in Settings → Integrations
      </a>
      {/* TODO(epic): inline OAuth reconnect flow */}
    </p>
  );

  return (
    <Modal show={show} onClose={onClose} size="lg" dismissible>
      <ModalHeader id={titleId}>Post deck</ModalHeader>
      <ModalBody data-testid="presentation-post-modal">
        {channelsLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : channels.length === 0 ? (
          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-200">
            <p>No connected channels found.</p>
            {reconnectBlock}
          </div>
        ) : step === 1 ? (
          <div data-testid="post-step-select" className="space-y-3" role="radiogroup" aria-labelledby={titleId}>
            <p className="text-sm text-gray-600 dark:text-gray-300">Choose where to post this deck.</p>
            {channels.some((c) => c.needsReconnect) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/30">
                {reconnectBlock}
              </div>
            )}
            <ul className="space-y-2">
              {channels.map((c) => (
                <li key={c.channelId}>
                  <button
                    type="button"
                    data-testid={`post-channel-${c.channelId}`}
                    onClick={() => setSelectedId(c.channelId)}
                    className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
                      selectedId === c.channelId
                        ? 'border-purple-600 bg-purple-50 dark:border-purple-500 dark:bg-purple-900/30'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                    }`}
                  >
                    <ChannelGlyph iconKey={c.iconKey} />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">{c.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {c.lastPostedAt ? `Last posted ${c.lastPostedAt}` : 'Never posted from Brazn'}
                      </div>
                      {c.needsReconnect && (
                        <div className="mt-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
                          Connection expired — reconnect required
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : step === 2 ? (
          <div data-testid="post-step-preview" className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Preview and add an optional note ({selected?.name ?? 'channel'}).
            </p>
            {selected?.needsReconnect ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/30">
                {reconnectBlock}
                <p className="mt-2 text-xs font-semibold text-amber-900 dark:text-amber-100">
                  Connection expired — reconnect required
                </p>
              </div>
            ) : null}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-900">
              <div className="aspect-video max-h-52 w-full overflow-hidden">
                {firstSlide ? (
                  <SlideCanvasThumbnail
                    slide={normalizeRenderedSlide(firstSlide)}
                    isActive={false}
                    slideNumber={1}
                    onSelect={() => {}}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-gray-500">No slide preview</div>
                )}
              </div>
              <p className="truncate border-t border-gray-200 px-3 py-2 text-sm font-medium dark:border-gray-600">
                {deckTitle}
              </p>
            </div>
            <div>
              <Label htmlFor="post-note">Add a note (optional)</Label>
              <Textarea
                id="post-note"
                data-testid="post-message-field"
                value={message}
                maxLength={MAX_MESSAGE}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a note…"
                rows={3}
                className="mt-1"
              />
              <p
                className={`mt-1 text-right text-xs tabular-nums ${
                  message.length >= MAX_MESSAGE
                    ? 'font-semibold text-red-600 dark:text-red-400'
                    : message.length >= Math.floor(MAX_MESSAGE * 0.9)
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-gray-500 dark:text-gray-400'
                }`}
                data-testid="post-message-counter"
              >
                {message.length} / {MAX_MESSAGE} chars
              </p>
            </div>
          </div>
        ) : (
          <div data-testid="post-step-result" className="space-y-3">
            {result?.ok ? (
              <div data-testid="post-success" className="text-center space-y-2">
                <p className="text-lg font-semibold text-green-700 dark:text-green-300">
                  Posted to {result.data.channelName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="post-success-time">
                  {formatPostedAgo(result.data.postedAt)}
                </p>
                {result.data.postUrl && (
                  <a
                    href={result.data.postUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block text-sm font-medium text-purple-600 underline dark:text-purple-400"
                    data-testid="post-success-link"
                  >
                    View post →
                  </a>
                )}
              </div>
            ) : (
              <div data-testid="post-error" className="text-center">
                <p className="text-lg font-semibold text-red-700 dark:text-red-300">Couldn&apos;t post to channel</p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{result?.ok === false ? result.msg : ''}</p>
                <Button
                  className="mt-4"
                  color="purple"
                  data-testid="post-error-retry"
                  onClick={() => {
                    setResult(null);
                    setStep(2);
                  }}
                >
                  Retry
                </Button>
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {channelsLoading || channels.length === 0 ? (
          <Button color="gray" onClick={onClose}>
            Close
          </Button>
        ) : step === 1 ? (
          <>
            <Button color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button color="purple" disabled={!selectedId} onClick={goPreview} data-testid="post-step1-continue">
              Continue
            </Button>
          </>
        ) : step === 2 ? (
          <>
            {channels.length > 1 ? (
              <Button color="gray" onClick={() => setStep(1)} data-testid="post-back-step1">
                <ArrowLeftIcon className="mr-1 h-4 w-4" />
                Back
              </Button>
            ) : (
              <Button color="gray" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button
              color="purple"
              disabled={posting || (selected?.needsReconnect ?? false)}
              onClick={() => void runPost()}
              data-testid="post-submit"
            >
              {posting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Posting…
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="mr-1 h-4 w-4" />
                  Post now
                </>
              )}
            </Button>
          </>
        ) : step === 3 ? (
          <>
            <Button color="gray" onClick={onClose}>
              Close
            </Button>
            {result?.ok ? (
              <Button
                color="purple"
                data-testid="post-again"
                onClick={() => {
                  clearCloseTimer();
                  setResult(null);
                  setMessage('');
                  setStep(channels.length > 1 ? 1 : 2);
                  if (channels.length === 1) setSelectedId(channels[0]?.channelId ?? null);
                }}
              >
                Post again
              </Button>
            ) : null}
          </>
        ) : null}
      </ModalFooter>
    </Modal>
  );
}
